import { MEDIA_STALE_TIME } from '@/utils/media';
import {
    MediaPackType,
    MediaProgramMetadata,
    MediaProgramWithGuides,
} from '@/utils/media/types';
import { getProgramId } from '@/utils/programs';
import { checkQueryCache, ItemToQueryMapping } from '@rhg/libs/react-query';
import { invalidatePacks, packsQueryOptions, usePacks } from '../use-packs';
import { invalidatePrograms, usePrograms } from '../use-programs';
import { ClinicalPacksResponseItem } from './types';

export type ClinicalPackEntry = {
    packClass: string;
    packProgram: MediaProgramMetadata;
    program: MediaProgramWithGuides | undefined;
};

// Invalidates all provided pack classes
// and invalidates all programs contained in those packs as well
// (useClinicalPacks returns both packs and programs)
export async function invalidateClinicalPacks({
    circleId,
    packClasses,
}: {
    circleId: number;
    packClasses: string[];
}) {
    const keyObjects = packClasses.map(
        (packClass): ItemToQueryMapping<string> => ({
            item: packClass,
            queryKey: packsQueryOptions.queryKey({
                packClasses: [packClass],
                circleId,
            }),
        })
    );
    const { cachedQueries } = checkQueryCache<string, MediaPackType[]>(
        keyObjects,
        MEDIA_STALE_TIME
    );
    const allProgramIds = cachedQueries
        .reduce<MediaPackType[]>(
            (cachedData, item) => [...cachedData, ...item.data],
            []
        )
        .reduce<string[]>(
            (acc, pack) => [
                ...acc,
                ...pack.items
                    .filter((item) => item.type === 'program')
                    .map((item) => item.programId),
            ],
            []
        );

    await invalidatePrograms({ programIds: new Set(allProgramIds), circleId });
    invalidatePacks({ packClasses, circleId });
}

export function useClinicalPacks(
    circleId: number,
    packClasses: string[] = [],
    isOrdered = false
) {
    const { packs: data, ...otherProps } = usePacks(packClasses, circleId);

    const programIds =
        data?.reduce<string[]>((acc, pack) => {
            const programIds = pack.items
                .filter((item) => item.type === 'program')
                .map((item) => item.programId);
            return [...acc, ...programIds];
        }, []) ?? [];
    const { programs, ...programOtherProps } = usePrograms(
        programIds,
        circleId
    );
    // Don't return items without first having the relevant programs
    if (programIds.length && !programOtherProps.isFetched) {
        return {
            clinicalPacks: { items: undefined },
            ...programOtherProps,
        };
    }
    const packsWithPrograms = data?.map((pack) => {
        const packProgram = pack.items?.find((item) => item.type === 'program');
        const programId = packProgram?.programId;
        const result: ClinicalPacksResponseItem = {
            packClassId: pack.packClass,
            packProgram: packProgram!,
            program: programs?.find(
                (program) => getProgramId(program) === programId
            ),
        };
        return result;
    });

    const items = isOrdered
        ? packClasses.reduce<ClinicalPacksResponseItem[]>(
              (orderedPackItems, packClass) => {
                  const packItem = packsWithPrograms?.find(
                      (packItem) => packItem.packClassId === packClass
                  );
                  if (packItem) {
                      return [...orderedPackItems, packItem];
                  } else {
                      return orderedPackItems;
                  }
              },
              []
          )
        : packsWithPrograms;

    return {
        clinicalPacks: { items },
        ...otherProps,
    };
}

export * from './use-active-circle-clinical-packs';
export * from './use-all-active-circle-clinical-packs';
export * from './use-all-clinical-packs';
